import {
  createContext, useContext, useState, type ReactNode, useCallback,
} from "react";
import { VoucherDetailsDrawer } from "@/features/vouchers/components/VoucherDetailsDrawer";
import type { VoucherEmployee } from "@/features/vouchers/types/voucherEmployee.type";

type VoucherDrawerContextType = {
  openVoucherDrawer: (voucher: VoucherEmployee) => void
  closeVoucherDrawer: () => void
  isDrawerOpen: boolean
  selectedVoucher: VoucherEmployee | null
};

const VoucherDrawerContext = createContext<VoucherDrawerContextType | undefined>(undefined);

type VoucherDrawerProviderProps = { children: ReactNode };

export const VoucherDrawerProvider = ({ children }: VoucherDrawerProviderProps) => {
  const [selectedVoucher, setSelectedVoucher] = useState<VoucherEmployee | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const openVoucherDrawer = useCallback((voucher: VoucherEmployee) => {
    setSelectedVoucher(voucher);
    setIsDrawerOpen(true);
  }, []);

  const closeVoucherDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const value: VoucherDrawerContextType = {
    openVoucherDrawer,
    closeVoucherDrawer,
    isDrawerOpen,
    selectedVoucher,
  };

  return (
    <VoucherDrawerContext.Provider value={value}>
      {children}
      <VoucherDetailsDrawer
        isOpen={isDrawerOpen}
        onClose={closeVoucherDrawer}
        voucher={selectedVoucher}
      />
    </VoucherDrawerContext.Provider>
  );
};

export const useVoucherDrawer = (): VoucherDrawerContextType => {
  const context = useContext(VoucherDrawerContext);
  if (!context) {
    throw new Error("useVoucherDrawer must be used within a VoucherDrawerProvider");
  }
  return context;
};
